import { inject, injectable } from 'inversify';
import { RpcSuccess } from '@dealroadshow/json-rpc-dispatcher';
import DITypes from '@creditflowresearch/json-rpc-dispatcher/dispatcher-cfr/DITypes';
import Request from '@framework/api/Rpc/Request';
import BaseRepository from '@/creditflowresearch/infrastructure/repository/BaseRepository';
import { IDataChart } from '@/creditflowresearch/domain/vo/Bond/IDataChart';
import { withServerCache } from '@/creditflowresearch/infrastructure/cache/withServerCache';
import type { IDispatcher } from '@creditflowresearch/json-rpc-dispatcher/dispatcher-cfr';

@injectable()
class BondsRepository extends BaseRepository {
  constructor(
    @inject(DITypes.JsonRpcDispatcher) protected readonly rpc: IDispatcher,
  ) {
    super(rpc);
  }

  getBwicList = async (payload) => this.abortableCall(
    new Request('finsight_web.market.bond.bwic.get_bwics', payload),
  );

  getInventoryList = async (payload) => this.abortableCall(
    new Request('finsight_web.market.bond.inventory.get_inventories', payload),
  );

  getCondorBwicList = async (payload) => this.abortableCall(
      new Request('finsight_web.bond.bwic.get_bwics', payload),
  );

  getCondorInventoryList = async (payload) => this.abortableCall(
      new Request('finsight_web.bond.inventory.get_inventories', payload),
  );

  getAssetClassList = async () => {
    let response = await this.rpc.call<RpcSuccess>(new Request('finsight_web.market.bond.asset_class.get_asset_classes', {}));
    return response.getResult().payload;
  };

  getTraceAssetClassList = async () => {
    let response = await this.rpc.call<RpcSuccess>(new Request('finsight_web.market.bond.asset_class.get_trace_asset_classes', {}));
    return response.getResult().payload;
  };

  deleteBwic = async (payload: {
    [key: string]: string,
  }) => {
    let response = await this.rpc.call<RpcSuccess>(new Request('finsight_web.bond.bwic.delete', payload));
    return response.getResult().payload;
  };

  deleteInventory = async (payload: {
  [key: string]: string,
}) => {
    let response = await this.rpc.call<RpcSuccess>(new Request('finsight_web.bond.inventory.delete', payload));
    return response.getResult().payload;
  };

  async getBondsFilter(filter) {
    const payload = { filter };
    let req = new Request('finsight_web.market.bond.filter.get_boundary_filter', payload);
    let response = await this.rpc.call<RpcSuccess>(req);
    return response.getResult().payload;
  }

  async getBondsIssuersFilter(filter, search) {
    const payload = { filter, search };
    let req = new Request('finsight_web.market.bond.filter.get_issuers', payload);
    let response = await this.rpc.call<RpcSuccess>(req);
    return response.getResult().payload;
  }

  async getBondsFilterByHash(hash) {
    const payload = { hash };
    let req = new Request('finsight_web.bond.filter.get_by_hash', payload);
    let response = await this.rpc.call<RpcSuccess>(req);
    return response.getResult().payload;
  }

  getBondAnalyticsContactsList = async (payload) => this.abortableCall(
      new Request('finsight_web.bond.investor.analytics.get_bi_investor_login_stats', payload),
  );

  getTraceAnalyticsContactsList = async (payload) => this.abortableCall(
      new Request('finsight_web.bond.investor.analytics.get_trace_investor_login_stats', payload),
  );

  @withServerCache({
    scope: 'Bonds',
    ttlSeconds: 180,
    maxEntries: 25,
    anonymousOnly: true,
  })
  async getBondsTile(payload) {
    let response = await this.rpc.call<RpcSuccess>(new Request('finsight_web.market.bond.get_bond_tile_data', payload));
    return response.getResult().payload;
  }

  getBwicSummaryList = async (payload) => {
    let response = await this.rpc.call<RpcSuccess>(new Request('finsight_web.market.bond.bwic.get_dealer_summary', payload));
    return response.getResult().payload;
  };

  getInventorySummaryList = async (payload) => {
    let response = await this.rpc.call<RpcSuccess>(new Request('finsight_web.market.bond.inventory.get_dealer_summary', payload));
    return response.getResult().payload;
  };

  getBwicDealerChartData = this.createRpcMethod<IDataChart[]>(
    'finsight_web.market.bond.bwic.get_bwics_chart_by_dealer',
  );

  getBwicSectorChartData = this.createRpcMethod<IDataChart[]>(
    'finsight_web.market.bond.bwic.get_bwics_chart_by_sector',
  );

  getInventoryDealerChartData = this.createRpcMethod<IDataChart[]>(
    'finsight_web.market.bond.inventory.get_inventories_chart_by_dealer',
  );

  getInventorySectorChartData = this.createRpcMethod<IDataChart[]>(
    'finsight_web.market.bond.inventory.get_inventories_chart_by_sector',
  );
}

export default BondsRepository;
